import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dispatch, RootState } from '../../../store/store';
import { useCalCertGuardedAction } from '../../../hooks/useCalCertGuardedAction';
import CalCertWarningModal from '../../CalCertWarningModal/CalCertWarningModal';

interface PdfGeneratorProps extends ReturnType<typeof mapState>, ReturnType<typeof mapDispatch> {
  disabled?: boolean;
}

const PdfGenerator: React.FC<PdfGeneratorProps> = ({
  downloading,
  generatePdf,
  updateController,
  disabled = false,
}) => {
  const { id: cycleId } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const {
    run: runCalCertGuardedAction,
    missingControllers: missingCalCertControllers,
    closeModal: closeCalCertModal,
    disableCheckAndRetry: disableCalCertCheckAndRetry,
    proceedWithoutCalCert,
  } = useCalCertGuardedAction(updateController);

  const handleDownload = async () => {
    try {
      await runCalCertGuardedAction((options) => generatePdf({ cycleId, skipCalCertCheck: options?.skipCalCertCheck }));
    } catch (error) {
      console.error(t('messages.errorDownloadingPdf'), error);
    }
  };

  return (
    <>
      <button
        aria-label={downloading ? t('buttons.downloading', 'Downloading...') : t('buttons.generatePdf', 'Generate PDF')}
        className="fixed bottom-2 left-8 right-8 z-50 h-12 w-[calc(100%-4rem)] rounded-md border-2 border-black-3 bg-primary text-xl font-bold text-white hover:bg-[#ff9146] disabled:opacity-50 lg:left-[329px] lg:w-[calc(100%-23rem)] xl:left-[337px] xl:lg:w-[calc(100%-25rem)]"
        onClick={handleDownload}
        disabled={downloading || disabled}
      >
        {downloading ? t('buttons.downloading', 'Downloading...') : t('buttons.generatePdf', 'Generate PDF')}
      </button>
      {missingCalCertControllers && (
        <CalCertWarningModal
          isVisible
          controllers={missingCalCertControllers}
          onClose={closeCalCertModal}
          onDisableCheck={disableCalCertCheckAndRetry}
          onProceedWithoutCalCert={proceedWithoutCalCert}
          proceedButtonText="Generate without Cal-Cert"
        />
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  downloading: state.reports.downloading,
});

const mapDispatch = (dispatch: Dispatch) => ({
  generatePdf: dispatch.reports.generatePdf,
  updateController: dispatch.controllers.updateController,
});

export default connect(mapState, mapDispatch)(PdfGenerator);
